import { isNumber, orderBy } from 'lodash'

import {
  Asset,
  DropdownListData,
  ModalEvent,
  TransactionSettings,
} from '@types'
import { AssetCategoryAccount } from '@/entities/assets/utils/enums'
import { EntityEvent } from '@/entities/utils/enums'
import { AssetClass } from '@/entities/assets'

import { NEGATIVE_RULES, POSITIVE_RULES } from '@/helpers/validate'
import { ASSET_FIELD, ASSET_CURRENCY_TYPE } from '@/entities/assets/utils/const'
import { DELIMETER_KEYWORD } from '@/components/UI/Dropdown/utils/const'
import { TRANSFERS } from './const'

import { useAssetsBunchStore } from '@/store/assets/bunch'
import { useModalsStore } from '@/store/modals'

const createAssetDropdownItem = (item: Asset, excludeLinkedAccounts?: boolean) => ({
  value: item.id,
  label: item.name,
  disabled: excludeLinkedAccounts && !!item.linked_account_id,
  meta: {
    ticker: item.ticker,
    search: `${item.name} ${item.ticker} ${item.type}`,
    type: item.type
  },
})

export const getAssetListValues = (
  settings: TransactionSettings['entries'][number]['asset'],
  assetsList: Asset[],
  opposingValue?: string | null,
  excludeLinkedAccounts?: boolean,
) => {
  if (settings?.value) {
    const item = assetsList.find(item => item.name === settings?.value)
    return item ? [createAssetDropdownItem(item, excludeLinkedAccounts)] : []
  }
  let list = assetsList
  if (settings?.includes) {
    list = list.filter(item => settings.includes?.includes(item.type))
  }
  if (opposingValue) {
    const index = list.findIndex(item => item.id === opposingValue)
    if (index >= 0) {
      list.splice(index, 1)
    }
  }
  let result
  if (list.find(item => TRANSFERS.includes(item.name.toLowerCase()))) {
    result = list.reduce((acc, item: Asset) => {
      const value = createAssetDropdownItem(item, excludeLinkedAccounts)
      if (TRANSFERS.includes(item.name.toLowerCase())) {
        acc.unshift(value, {
          value: DELIMETER_KEYWORD,
          label: DELIMETER_KEYWORD,
        })
        return acc
      }
      acc.push(value)
      return acc
    }, [] as DropdownListData[])
  } else {
    result = list.map(item => createAssetDropdownItem(item, excludeLinkedAccounts))
  }
  return orderBy(result, ['label']) as DropdownListData[]
}

export const preSetAsset = (
  assetId: string | null | undefined,
  settings: TransactionSettings['entries'][number]['asset'] | undefined,
  assetsBunchStore: ReturnType<typeof useAssetsBunchStore>,
  currency: string | undefined,
  defaultValue: string | undefined = undefined,
) => {
  if (!settings) return
  if (assetId) {
    const asset = assetsBunchStore.getElementById(assetId)
    const type = asset?.field<string>(ASSET_FIELD.TYPE).value
    if (type && settings?.includes?.includes(type)) {
      return assetId
    }
  }
  if (settings?.includes?.[0] === ASSET_CURRENCY_TYPE) {
    return currency
  } else {
    const asset =
      assetsBunchStore.getElementByName(settings?.default || '') ||
      assetsBunchStore.getElementByName(settings?.value || '')
    return asset?.id || defaultValue
  }
}

export const preSetAmount = (
  amount: number | null,
  settings: TransactionSettings['entries'][number]['amount'] | undefined,
) => {
  if (!settings) return null
  if (settings.rule === 'percent') {
    return 1
  }
  if (isNumber(amount)) {
    if (NEGATIVE_RULES.includes(settings.rule)) {
      return Math.abs(amount) * -1
    }
    if (POSITIVE_RULES.includes(settings.rule)) {
      return Math.abs(amount)
    }
    return amount
  }
  return settings.default || null
}

export const onCreateAsset = (
  value: string,
  options: {
    restrictions: string[] | undefined,
    hideEODSearch?: boolean,
    equalizeTicker?: boolean
  },
  instance: AssetClass,
  modalsStore: ReturnType<typeof useModalsStore>,
  callback: (data: string) => void,
) => {
  let type = ''
  const { restrictions } = options
  if (
    restrictions?.length === 2 &&
    restrictions?.includes(ASSET_CURRENCY_TYPE)
  ) {
    type = ASSET_CURRENCY_TYPE
  } else if (restrictions?.includes(AssetCategoryAccount.INCOME)) {
    type = AssetCategoryAccount.INCOME
  } else if (restrictions?.includes(AssetCategoryAccount.EXPENSE)) {
    type = AssetCategoryAccount.EXPENSE
  } else {
    type = restrictions?.length === 1 ? restrictions[0] : ''
  }
  instance.set({
    type,
    name: value,
    ticker: value,
  })
  instance.isDialog = true
  instance.addEventListener(EntityEvent.STORED, data => {
    instance.isDialog = false
    callback(data.id)
  })
  const modalInstance = modalsStore.init(instance.id, instance)
  modalInstance?.open(modalsStore.getZIndex(), options)
  modalInstance?.addEventListener(ModalEvent.CLOSE, () => {
    if (!instance.isNew) return
    instance.remove()
  })
}
